import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Home, Users, ShoppingBag, Upload, FolderOpen, Mic,
  Shield, Menu, X, Download, BarChart3,
  Video, Bot, Sparkles, Film, Phone, Bell, Settings, ScrollText, MessageSquare, Crown, Palette, Library, Wand2, ImageIcon, Share2, LayoutDashboard, ChevronDown, Lock
} from 'lucide-react';
import SubscriptionBanner from './SubscriptionBanner';
import LanguageSwitcher from './LanguageSwitcher';
import CurrencySwitcher from './CurrencySwitcher';
import usePresenceHeartbeat from '../hooks/usePresenceHeartbeat';

const buildNavSections = (t) => ([
  {
    label: null,
    items: [
      { path: '/feed', label: t('nav.feed'), icon: Home, key: 'feed' },
      { path: '/communities', label: t('nav.communities'), icon: Users, key: 'communities' },
      { path: '/messages', label: t('nav.messages'), icon: MessageSquare, key: 'messages' },
    ],
  },
  {
    label: t('nav.create'),
    items: [
      { path: '/create', label: t('nav.ai_creator'), icon: Wand2, accent: true, requiresAI: true, key: 'ai-creator' },
      { path: '/design-studio', label: t('nav.design_studio'), icon: Palette, key: 'design-studio' },
      { path: '/wallpapers', label: t('nav.wallpapers'), icon: ImageIcon, key: 'wallpapers' },
      { path: '/my-designs', label: t('nav.my_designs'), icon: Library, key: 'my-designs' },
    ],
  },
  {
    label: t('nav.sell'),
    items: [
      { path: '/marketplace', label: t('nav.marketplace'), icon: ShoppingBag, key: 'marketplace' },
      { path: '/creator-dashboard', label: t('nav.creator_dashboard'), icon: BarChart3, key: 'creator-dashboard' },
      { path: '/dashboard', label: t('nav.business_dashboard'), icon: LayoutDashboard, accent: true, key: 'business-dashboard' },
    ],
  },
  {
    label: t('nav.ai_studio'),
    requiresAI: true,
    items: [
      { path: '/assistant', label: t('nav.ai_assistant'), icon: Bot, requiresAI: true, key: 'ai-assistant' },
      { path: '/image-generator', label: t('nav.image_generator'), icon: Sparkles, requiresAI: true, key: 'image-generator' },
      { path: '/video-generator', label: t('nav.video_generator'), icon: Film, requiresAI: true, key: 'video-generator' },
      { path: '/voice', label: t('nav.voice_studio'), icon: Mic, requiresAI: true, key: 'voice-studio' },
    ],
  },
  {
    label: t('nav.library'),
    items: [
      { path: '/downloads', label: t('nav.downloads'), icon: Download, key: 'downloads' },
      { path: '/upload', label: t('nav.upload'), icon: Upload, key: 'upload' },
      { path: '/storage', label: t('nav.storage'), icon: FolderOpen, key: 'storage' },
    ],
  },
  {
    label: t('nav.connect'),
    items: [
      { path: '/social-connections', label: t('nav.social_links'), icon: Share2, key: 'social-links' },
      { path: '/video-call', label: t('nav.calls'), icon: Phone, key: 'calls' },
    ],
  },
]);

export default function Layout({ children }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { hasAIAccess, isOwner, planName } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const navSections = buildNavSections(t);

  // On first login only, apply the user's server-stored preferred_language so new devices
  // pick up the user's saved choice. Subsequent in-session switches via LanguageSwitcher
  // win and are not reverted (LanguageSwitcher persists to server itself).
  const appliedForUserRef = useRef(null);
  useEffect(() => {
    const pref = user?.preferred_language;
    if (user?.id && pref && appliedForUserRef.current !== user.id) {
      appliedForUserRef.current = user.id;
      if (pref !== (i18n.language || '').split('-')[0]) {
        i18n.changeLanguage(pref);
      }
    }
  }, [user?.id, user?.preferred_language, i18n]);

  // Keep last_seen fresh on the server so the Calls page sees accurate presence
  // for this user even if the WebSocket reconnects or is still attaching.
  usePresenceHeartbeat(user?.id);

  const toggleSection = (label) => {
    setCollapsedSections(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const SidebarContent = ({ onNavigate }) => (
    <>
      {/* Profile Card */}
      {user && (
        <div className="px-4 pt-5 pb-4 border-b border-amber-100/50" data-testid="sidebar-profile">
          <Link to={`/profile/${user.id}`} onClick={onNavigate} className="flex items-center gap-3 group">
            <Avatar className="h-10 w-10 border-2 border-amber-200/60 group-hover:border-amber-400 transition-colors">
              <AvatarFallback className="bg-gradient-to-br from-amber-50 to-amber-100 text-amber-700 font-serif text-base">
                {(user.display_name || user.username || 'U')[0].toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-sm font-medium text-obsidian truncate">{user.display_name || user.username}</p>
              <p className="text-[10px] text-amber-600 capitalize">
                {isOwner ? 'Platform Owner'
                  : planName !== 'Free' ? planName
                  : user.subscription_status === 'trial' ? 'Free Trial'
                  : 'Free Account'}
              </p>
            </div>
          </Link>
        </div>
      )}

      {/* Inner Circle CTA */}
      <div className="px-3 pt-4 pb-1">
        <Link
          to="/inner-circle"
          onClick={onNavigate}
          data-testid="nav-inner-circle"
          className={`flex items-center gap-2.5 px-3 py-2 text-xs rounded-lg transition-all duration-300 ${
            location.pathname === '/inner-circle'
              ? 'bg-amber-500 text-white shadow-md shadow-amber-200/50'
              : 'bg-gradient-to-r from-amber-50 to-amber-100/60 text-amber-700 border border-amber-200/50 hover:border-amber-300 hover:shadow-sm'
          }`}
        >
          <Crown size={14} />
          <span className="font-medium tracking-wide">{t('nav.inner_circle') !== 'nav.inner_circle' ? t('nav.inner_circle') : 'Join Inner Circle'}</span>
        </Link>
      </div>

      {/* Nav Sections */}
      <nav className="px-3 py-3 space-y-1 flex-1" data-testid="sidebar-nav">
        {navSections.map((section, si) => {
          const isCollapsed = section.label ? collapsedSections[section.label] : false;
          return (
            <div key={si} className={section.label ? 'pt-3' : ''}>
              {section.label && (
                <button
                  onClick={() => toggleSection(section.label)}
                  className="flex items-center justify-between w-full px-3 mb-1 group"
                >
                  <span className="text-[9px] uppercase tracking-[0.2em] text-amber-600/70 font-semibold flex items-center gap-1.5">
                    {section.label}
                    {section.requiresAI && !hasAIAccess && !isOwner && <Lock size={8} className="text-stone-300" />}
                  </span>
                  <ChevronDown size={10} className={`text-amber-400/60 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`} />
                </button>
              )}
              {!isCollapsed && section.items.map(item => {
                const active = location.pathname === item.path || (item.path !== '/feed' && location.pathname.startsWith(item.path + '/'));
                const locked = item.requiresAI && !hasAIAccess && !isOwner;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onNavigate}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-2.5 px-3 py-2 text-xs rounded-lg transition-all duration-200 ${
                      active
                        ? 'bg-obsidian text-white shadow-sm'
                        : locked
                          ? 'text-stone-300 hover:text-stone-400 hover:bg-stone-50/50'
                          : item.accent
                            ? 'text-amber-700 bg-amber-50/50 hover:bg-amber-100/60 border border-amber-100/50'
                            : 'text-obsidian/60 hover:text-obsidian hover:bg-stone-50'
                    }`}
                  >
                    <item.icon size={14} className={active ? 'text-amber-400' : locked ? 'text-stone-300' : item.accent ? 'text-amber-500' : ''} />
                    <span className="font-medium flex-1">{item.label}</span>
                    {locked && <Lock size={11} className="text-stone-300 shrink-0" />}
                  </Link>
                );
              })}
            </div>
          );
        })}

        {/* Admin */}
        {user?.role === 'admin' && (
          <div className="pt-3">
            <span className="px-3 text-[9px] uppercase tracking-[0.2em] text-red-400/70 font-semibold">{t('nav.admin')}</span>
            <Link
              to="/admin"
              onClick={onNavigate}
              data-testid="nav-admin"
              className={`flex items-center gap-2.5 px-3 py-2 text-xs rounded-lg transition-all duration-200 mt-1 ${
                location.pathname === '/admin'
                  ? 'bg-obsidian text-white shadow-sm'
                  : 'text-obsidian/60 hover:text-obsidian hover:bg-stone-50'
              }`}
            >
              <Shield size={14} className={location.pathname === '/admin' ? 'text-amber-400' : ''} />
              <span className="font-medium">{t('nav.admin')}</span>
            </Link>
          </div>
        )}
      </nav>

      {/* Bottom: Settings + Notifications */}
      <div className="px-3 pb-4 pt-2 border-t border-amber-100/40 space-y-1 mt-auto">
        <Link
          to="/notifications"
          onClick={onNavigate}
          data-testid="nav-notifications"
          className={`flex items-center gap-2.5 px-3 py-2 text-xs rounded-lg transition-all duration-200 ${
            location.pathname === '/notifications' ? 'bg-obsidian text-white' : 'text-obsidian/50 hover:text-obsidian hover:bg-stone-50'
          }`}
        >
          <Bell size={14} />
          <span className="font-medium">{t('nav.notifications') !== 'nav.notifications' ? t('nav.notifications') : 'Notifications'}</span>
        </Link>
        <Link
          to="/settings"
          onClick={onNavigate}
          data-testid="nav-settings"
          className={`flex items-center gap-2.5 px-3 py-2 text-xs rounded-lg transition-all duration-200 ${
            location.pathname === '/settings' ? 'bg-obsidian text-white' : 'text-obsidian/50 hover:text-obsidian hover:bg-stone-50'
          }`}
        >
          <Settings size={14} />
          <span className="font-medium">{t('nav.settings')}</span>
        </Link>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-stone-50/50 font-sans">
      {/* Top Bar */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-amber-100/40">
        <div className="max-w-[1440px] mx-auto flex items-center justify-between h-14 px-4 md:px-6">
          <div className="flex items-center gap-3">
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-1.5 hover:bg-amber-50 rounded-lg transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <Link to={user ? '/feed' : '/'} className="font-serif text-base tracking-tight" data-testid="logo-link">
              <span className="text-obsidian">Susan David</span>
              <span className="text-amber-500 ml-1.5 font-light">Creator Platform</span>
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <CurrencySwitcher />
            <LanguageSwitcher />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                    <Avatar className="h-8 w-8 border-2 border-amber-100">
                      <AvatarFallback className="bg-gradient-to-br from-amber-50 to-amber-100 text-amber-700 text-xs font-serif">
                        {(user.display_name || user.username || 'U')[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:block text-xs font-medium text-obsidian">{user.display_name || user.username}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 rounded-lg shadow-xl border-amber-100/50">
                  <DropdownMenuItem onClick={() => navigate(`/profile/${user.id}`)} data-testid="menu-profile" className="text-xs">
                    {t('nav.profile') !== 'nav.profile' ? t('nav.profile') : 'Profile'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings" className="text-xs">
                    <Settings size={12} className="mr-2" /> {t('nav.settings')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/pricing')} className="text-xs">
                    <Crown size={12} className="mr-2" /> {t('pricing.membership') !== 'pricing.membership' ? t('pricing.membership') : 'Membership'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/rules')} data-testid="menu-rules" className="text-xs">
                    <ScrollText size={12} className="mr-2" /> {t('nav.community_rules') !== 'nav.community_rules' ? t('nav.community_rules') : 'Community Rules'}
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin" className="text-xs">
                      <Shield size={12} className="mr-2" /> {t('nav.admin')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid="menu-logout" className="text-xs text-red-600">
                    {t('common.sign_out')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button data-testid="header-login-btn" className="rounded-none text-[10px] uppercase tracking-[0.15em] h-9 px-6 bg-obsidian text-white hover:bg-amber-600 transition-colors">
                  {t('common.sign_in')}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto flex">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex md:flex-col w-56 shrink-0 sticky top-14 h-[calc(100vh-3.5rem)] bg-white border-r border-amber-100/40 overflow-y-auto" data-testid="desktop-sidebar">
          <SidebarContent onNavigate={() => {}} />
        </aside>

        {/* Mobile Nav Overlay */}
        {mobileOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
            <aside className="absolute left-0 top-14 bottom-0 w-64 bg-white border-r border-amber-100/40 shadow-2xl overflow-y-auto flex flex-col">
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-[calc(100vh-3.5rem)]">
          <SubscriptionBanner user={user} />
          <div className="p-4 md:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
